import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  TraderType,
  yearlyAnalysisRequestApi,
  YearlyAnalysisTableDataType,
} from '../../../api/apiConfig';
import * as Styled from '../TableStyled';
import {
  formatDashFormToEightDigitString,
  getMonthStartEnd,
} from '../../../function/replaceString';
import { BsArrowDown, BsArrowUp } from 'react-icons/bs';
import { useHeaderButtons } from '../../header/HeaderButtonProvider';
import SquareButton from '../../button/SquareButton';
import { Pagination } from '../Pagination';
import TableConfig from '../TableConfig';
import useFetch from '../../../api/useFetch';
import { useExcelDownload } from '../../../function/createExcel';

//더미데이터 생성용
// const getRandomNumber = (min: number, max: number): number =>
//   Math.floor(Math.random() * (max - min + 1)) + min;

// const getRandomDivision = (): string => {
//   const divisions = ['양초', '화장지', '양초, 화장지'];
//   return divisions[Math.floor(Math.random() * divisions.length)];
// };

// const generateData = (count: number): YearlyAnalysisTableDataType[] => {
//   return Array.from({ length: count }, (_, index) => ({
//     custDes: `거래처_${index + 1}`,
//     qty: getRandomNumber(100, 300),
//     outPrice: getRandomNumber(10000000, 20000000),
//     division: getRandomDivision(),
//   }));
// };

const excelColumns = [
  {
    header: '순번',
    key: 'No',
    columnLetter: 'A',
    width: 10,
  },
  {
    header: '판매처명',
    key: 'custDes',
    columnLetter: 'B',
    width: 50,
  },
  {
    header: '구매횟수',
    key: 'qty',
    columnLetter: 'C',
    width: 20,
  },
  {
    header: '구분',
    key: 'division',
    columnLetter: 'D',
    width: 21.5,
  },
  {
    header: '매출액',
    key: 'outPrice',
    columnLetter: 'E',
    width: 20,
  },
  {
    header: '비고',
    key: 'remark',
    columnLetter: 'F',
    width: 23,
  },
];

export const YearlyAnalysisTable = () => {
  const divs = ['양초', '화장지', '핸드타올'];

  const { setButtons } = useHeaderButtons();

  const { generate } = useExcelDownload();

  const theme = useTheme();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const { fetch: fetchYearlyAnalysisRequestApi } = useFetch({
    fetchFunction: yearlyAnalysisRequestApi,
    onSuccess: (data: YearlyAnalysisTableDataType[]) =>
      setData(data.sort((a, b) => b.outPrice - a.outPrice)),
  });

  function formatPrice(price: number): string {
    if (price <= 1000000) {
      return '100만 원 이하';
    }
    const base = Math.floor(price / 1000000) * 100; // 100만 원 단위 변환
    return `${base.toLocaleString()}만 원 이상`;
  }

  const [selectedDivs, setSelectedDivs] = useState<string[]>(divs);

  const [data, setData] = useState<YearlyAnalysisTableDataType[]>([]);

  const [periodStartDate, setPeriodStartDate] = useState<string>(
    getMonthStartEnd(new Date()).startDash
  );

  const [periodEndDate, setPeriodEndDate] = useState<string>(
    getMonthStartEnd(new Date()).endDash
  );

  //테이블 옵션값 ~~~
  const [headerOverflowType, setHeaderOverflowType] = useState<'wrap' | 'hide'>(
    'wrap'
  );
  const [bodyOverflowType, setBodyOverflowType] = useState<'wrap' | 'hide'>(
    'wrap'
  );
  const [bodyAlignHoriType, setBodyAlignHoriType] = useState<
    'left' | 'center' | 'right'
  >('center');

  const [cellHoriPaddValue, setCellHoriPaddValue] = useState<number>(5);
  const [bodyVertPaddValue, setBodyVertPaddValue] = useState<number>(5);

  const [showConfig, setShowConfig] = useState<boolean>(false);

  const columns = useMemo<ColumnDef<YearlyAnalysisTableDataType>[]>(
    () => [
      {
        header: 'No',
        id: 'NO',
        size: 46,
        cell: ({ cell, row, getValue }) => (
          <Styled.NoBodyCell>{row.index + 1}</Styled.NoBodyCell>
        ),
      },

      {
        header: '판매처명',
        accessorKey: 'custDes',
        size: 390,
      },
      {
        header: '구매횟수',
        accessorKey: 'qty',
        size: 95,
      },
      {
        header: '구분',
        accessorKey: 'division',
        size: 178,
      },
      {
        header: '매출액',
        accessorKey: 'outPrice',
        size: 200,
        cell: ({ row }) => (
          <>{`${(row.original.outPrice as number).toLocaleString()}원`}</>
        ),
      },
      {
        header: '비고',
        accessorKey: '',
        size: 215,
        cell: ({ row }) => <>{formatPrice(row.original.outPrice as number)}</>,
      },
    ],
    []
  );

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='조회하기'
          colorType='ACTIVE'
          onClick={() =>
            fetchYearlyAnalysisRequestApi({
              startDt: formatDashFormToEightDigitString(periodStartDate),
              endDt: formatDashFormToEightDigitString(periodEndDate),
              filterItems: selectedDivs,
            })
          }
        />
        <SquareButton
          text='엑셀저장'
          colorType='ACTIVE'
          onClick={() => {
            const excelData = data.map((origin) => ({
              ...origin,
              remark: formatPrice(origin.outPrice),
            }));
            generate(excelData, excelColumns, 'testaaa');
          }}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [setButtons, data, selectedDivs, periodStartDate, periodEndDate]);

  //아마 페이지네이션
  function useSkipper() {
    const shouldSkipRef = useRef(true);
    const shouldSkip = shouldSkipRef.current;

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = useCallback(() => {
      shouldSkipRef.current = false;
    }, []);

    useEffect(() => {
      shouldSkipRef.current = true;
    });

    return [shouldSkip, skip] as const;
  }

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex,
    getPaginationRowModel: getPaginationRowModel(),
    paginateExpandedRows: false,
    onPaginationChange: setPagination,
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const DivsArea = () => {
    return (
      <DivsContainer>
        {divs.map((division) => (
          <DivsItem
            onClick={() => {
              setSelectedDivs((prev) =>
                prev.includes(division)
                  ? selectedDivs.length === 1
                    ? prev
                    : prev.filter((prevDiv) => prevDiv !== division)
                  : [...prev, division]
              );
            }}
            $included={selectedDivs.includes(division)}
          >
            {division}
          </DivsItem>
        ))}
      </DivsContainer>
    );
  };

  return (
    <div>
      <PeriodContainer>
        <Row>
          <RowLabel>구분</RowLabel>

          <DivsArea />
        </Row>
        <Row>
          <RowLabel>조회시작일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodStartDate}
              onChange={(e) => {
                setPeriodStartDate(e.target.value);
              }}
            />
          </RowContent>
          <RowLabel>조회종료일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodEndDate}
              onChange={(e) => {
                setPeriodEndDate(e.target.value);
              }}
            />
          </RowContent>
        </Row>
      </PeriodContainer>
      <Pagination
        table={table}
        pagination={pagination}
        setPageSize={(pageSize: number) =>
          setPagination((prev) => ({
            pageIndex: prev.pageIndex,
            pageSize: pageSize,
          }))
        }
      />
      <Styled.TableContainer style={{ paddingTop: '25px' }}>
        <Styled.TableOptionButton onClick={() => setShowConfig(!showConfig)}>
          설정
        </Styled.TableOptionButton>
        <TableConfig
          show={showConfig}
          Header_Overflow_Type={headerOverflowType}
          setHeaderOverflowType={(value: 'hide' | 'wrap') =>
            setHeaderOverflowType(value)
          }
          Body_Overflow_Type={bodyOverflowType}
          setBodyOverflowType={(value: 'hide' | 'wrap') =>
            setBodyOverflowType(value)
          }
          Body_Align_Hori_Type={bodyAlignHoriType}
          setBodyAlignHoriType={(value: 'left' | 'center' | 'right') =>
            setBodyAlignHoriType(value)
          }
          Cell_Hori_Padd_Value={cellHoriPaddValue}
          setCellHoriPaddValue={(value: number) => setCellHoriPaddValue(value)}
          Body_Vert_Padd_Value={bodyVertPaddValue}
          setBodyVertPaddValue={(value: number) => setBodyVertPaddValue(value)}
        />
        <Styled.TableDiv
          {...{
            style: {
              ...columnSizeVars,
              width: table.getTotalSize(),
              // marginTop: '25px',
            },
          }}
          $headOverflowType={headerOverflowType}
          $bodyOverflowType={bodyOverflowType}
          $bodyAlignHoriType={bodyAlignHoriType}
          $cellHoriPaddValue={cellHoriPaddValue}
          $bodyVertPaddValue={bodyVertPaddValue}
        >
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <Styled.ThDiv {...{ key: headerGroup.id }}>
                {headerGroup.headers.map((headerCell) =>
                  headerCell.isPlaceholder ? null : (
                    <Styled.ThCell
                      {...{
                        key: headerCell.id,
                        style: {
                          width: `calc(var(--header-${headerCell?.id}-size) * 1px)`,
                          cursor: headerCell.column.getCanSort()
                            ? 'pointer'
                            : 'auto',
                        },
                        // headerCell.column.getCanSort()
                        className: 'th',
                      }}
                    >
                      {headerCell.isPlaceholder
                        ? null
                        : flexRender(
                            headerCell.column.columnDef.header,
                            headerCell.getContext()
                          )}

                      <Styled.ResizeHandler
                        {...{
                          onMouseDown: headerCell.getResizeHandler(),
                          className: `resizer ${
                            headerCell.column.getIsResizing()
                              ? 'isResizing'
                              : ''
                          }`,
                        }}
                      />
                    </Styled.ThCell>
                  )
                )}
              </Styled.ThDiv>
            );
          })}
          {table.getRowModel().rows.map((row) => (
            <>
              <Styled.TdDiv
                {...{
                  key: row.id,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isMainRow = row.depth === 0;

                  // 커스텀 스타일 (변경된 셀에 하이라이트 적용)
                  return (
                    <Styled.TdCell
                      {...{
                        key: cell.id,
                        className: 'td',
                        style: {
                          // 해당 셀이 변경되었으면 배경 색을 노란색으로 하이라이트
                          backgroundColor: isMainRow
                            ? theme.colors.white_odd
                            : 'transparent',
                          width: `calc(var(--header-${cell.column.id}-size) * 1px)`, // 셀 크기
                        },
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Styled.TdCell>
                  );
                })}
              </Styled.TdDiv>
            </>
          ))}
        </Styled.TableDiv>
      </Styled.TableContainer>
    </div>
  );
};

const PeriodContainer = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-items: stretch;

  &:not(:last-child) {
    border-bottom: 1px solid #b8b8b8;
  }
`;
const RowLabel = styled.div`
  flex: 0 0 120px;
  background-color: #d9d9d9;
  font-size: 15px;
  align-content: center;
  text-align: center;
  padding: 4px 0px;
  font-weight: 700;
`;

const RowContent = styled.div`
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const DateInput = styled.input`
  width: 100%;
  color: #595b60;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  box-sizing: border-box;
  border: none;
  cursor: pointer;

  /* Webkit 브라우저에서 달력 아이콘 숨기기 */
  /* &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  /* Firefox 브라우저에서 달력 아이콘 숨기기 */
  /*-moz-appearance: textfield; */
`;

const DivsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 9px;
  width: 100%;
  padding: 5px 10px;
  background-color: white;
`;

const DivsItem = styled.div<{ $included: boolean }>`
  width: fit-content;
  padding: 3px 7px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.charcoal};
  border: 1px solid black;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontsize.header_semi};
  cursor: pointer;
  background-color: ${({ $included }) =>
    $included ? '#d9f0ff' : 'transparent'};
`;
